import type { EOB, PaymentFile, PaymentFileExpandableRowProps } from '../types'
import type { ColumnsType } from 'antd/es/table'
import type { RenderExpandIcon } from 'rc-table/es/interface'

import {
  FileImageOutlined,
  FlagFilled,
  InfoCircleOutlined,
  PauseCircleFilled
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, Checkbox, Input, Modal, notification, Skeleton, Table, Tag, Tooltip } from 'antd'
import cx from 'classnames'
import React, { useCallback, useContext, useMemo, useReducer } from 'react'

import $api, { API_URL } from '../../../../API'
import { useAppDispatch } from '../../../../store'
import * as previewActions from '../../../../store/previewer'
import type { Attachment } from '../../../../store/previewer/types'
import {
  baseCellClassName,
  baseHeaderCellClassName,
  BatchSelectionContext,
  paymentFileExpandableRowInitialState,
  paymentMethods
} from '../constants'
import { currencyFormatter, pluralize } from '../functions'
import { paymentFileExpandableRowReducer } from '../store'

import { BatchActionButton } from './batch-action-button'
import { BatchSelectionPanel } from './batch-selection-panel'
import { EmptyCellValue } from './empty-cell-value'
import { ExpandIcon } from './expand-icon'
import { EOBExpandableRow } from './eob-expandable-row'
import dayjs from 'dayjs'
import { Duplicate } from '../../../../components/Icons/Duplicate/Duplicate'

type QueryKeyWithParams<T> = T extends Array<string | infer U> ? [string, U] : never

export const PaymentFileExpandableRow = (props: PaymentFileExpandableRowProps): JSX.Element => {
  const [state, dispatch] = useReducer(
    paymentFileExpandableRowReducer,
    paymentFileExpandableRowInitialState
  )

  const [paymentMethodFilters, setPaymentMethodFilters] = React.useState<string[]>([])
  const [filterDropdownOpen, setFilterDropdownOpen] = React.useState(false)
  const [pagination, setPagination] = React.useState({
    currentPage: 1,
    pageSize: 10
  })

  const query = useQuery({
    queryKey: [
      'check-run-eobs',
      {
        checkRunId: props.checkRunId,
        groupId: props.groupId,
        paymentFileId: props.paymentFileId,
        page: pagination.currentPage - 1,
        pageSize: pagination.pageSize
      }
    ],

    async queryFn({ queryKey, signal }) {
      const [, { checkRunId, groupId, paymentFileId, page, pageSize }] =
        queryKey as QueryKeyWithParams<typeof queryKey>

      const searchParams = new URLSearchParams([
        ['page', page.toString()],
        ['pageSize', pageSize.toString()]
      ])

      const response = await $api.get<{
        id: string
        eobs: EOB[]
        events: PaymentFile['events']
        eobs_total_count: number
      }>(
        `/check-runs/${checkRunId}/groups/${groupId}/payment-files/${paymentFileId}?${searchParams.toString()}`,
        {
          signal
        }
      )

      return response.data
    }
  })

  const eobs = useMemo(() => query?.data?.eobs ?? [], [query?.data?.eobs])
  const events = query?.data?.events

  const queryClient = useQueryClient()
  const appDispatch = useAppDispatch()

  const handlePdfPreviewClick = useCallback(
    (file: Attachment) => (): void => {
      appDispatch(previewActions.open(file))
    },
    [appDispatch]
  )

  const handlePropagatedActionClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>): void => {
      event.stopPropagation()
    },
    []
  )

  const token = window.localStorage.getItem('token')

  const buildFileHref = useCallback(
    (checkId: string) => {
      return `${API_URL}/pinnacle/x937/checks/${checkId}/images?token=${encodeURIComponent(token ?? '')}`
    },
    [token]
  )

  const columns = useMemo<ColumnsType<EOB>>(
    () => [
      {
        title: 'EOB #',
        dataIndex: 'id',
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Zelis Payment Method',
        dataIndex: 'zelis_payment_method',
        align: 'center',
        render(_, record) {
          if (typeof record.zelis_payment_method !== 'string') {
            return <EmptyCellValue />
          }

          return record.zelis_payment_method
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Payment Method',
        dataIndex: 'payment_method',
        align: 'center',
        filteredValue: paymentMethodFilters,
        filterDropdownOpen: filterDropdownOpen,
        onFilterDropdownOpenChange: open => {
          setFilterDropdownOpen(open)
        },
        filterDropdown: () => (
          <div className='p-2 min-w-[12rem]' onClick={e => e.stopPropagation()}>
            <div className='flex flex-col gap-2'>
              {paymentMethods.map(pm => (
                <Checkbox
                  key={pm.value}
                  checked={paymentMethodFilters.includes(pm.value)}
                  onChange={e => {
                    e.stopPropagation()
                    const newFilters = e.target.checked
                      ? [...paymentMethodFilters, pm.value]
                      : paymentMethodFilters.filter(f => f !== pm.value)
                    setPaymentMethodFilters(newFilters)
                  }}
                >
                  {pm.value}
                </Checkbox>
              ))}
            </div>
          </div>
        ),
        onFilter: (value, record) => {
          return record.payment_method === value
        },
        render(_, record) {
          if (!record.payment_method) {
            return <EmptyCellValue />
          }

          return record.payment_method
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Check Amount',
        dataIndex: 'check_amount',
        align: 'right',
        render(value: number) {
          return currencyFormatter.format(value)
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Check #',
        dataIndex: 'check_number',
        align: 'center',
        render(value: string) {
          if (!value) {
            return <EmptyCellValue />
          }

          if (value === 'mixed') {
            return (
              <Tag color='orange' className='font-lato size-xs'>
                Mixed
              </Tag>
            )
          }

          return value
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Paid Date',
        dataIndex: 'x937_paid_date',
        align: 'center',
        render(value: string, record) {
          if (!value || !record.x937_check_id) {
            return <EmptyCellValue />
          }

          if (value === 'mixed' || record.x937_check_id === 'mixed') {
            return (
              <Tag color='orange' className='font-lato size-xs'>
                Mixed
              </Tag>
            )
          }

          return (
            <div className='flex flex-row gap-2 justify-center items-center'>
              {dayjs(value).format('MM/DD/YYYY')}

              <div onClick={handlePropagatedActionClick}>
                <Tooltip title='View Checks'>
                  <button
                    type='button'
                    onClick={handlePdfPreviewClick({
                      mimeType: 'image/tiff',
                      name: 'Check images',
                      href: buildFileHref(record.x937_check_id)
                    })}
                  >
                    <FileImageOutlined className='text-lg text-blue-500 cursor-pointer hover:opacity-75' />
                  </button>
                </Tooltip>
              </div>
            </div>
          )
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Flags',
        key: 'flags',
        align: 'center',
        render(_, record) {
          const hasIneligible = record.has_ineligible_claims

          return (
            <div className='flex gap-2 justify-center items-center'>
              {record.is_on_hold && (
                <Tooltip title={`On hold due to "${record.on_hold_reason}"`}>
                  <PauseCircleFilled className='text-sm text-yellow-500' />
                </Tooltip>
              )}

              {record.is_flagged_stop_loss && (
                <Tooltip title='Flagged as stop loss'>
                  <FlagFilled className='text-sm text-lucent-red' />
                </Tooltip>
              )}

              {record.has_multiple_patients && (
                <Tooltip title='Claim Duplicates: multiple patients per claim'>
                  <Duplicate />
                </Tooltip>
              )}

              {hasIneligible && (
                <Tooltip title='Contains ineligible claims (I2954 or invalid amounts)'>
                  <InfoCircleOutlined className='text-sm text-gray-400' />
                </Tooltip>
              )}
            </div>
          )
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      }
    ],
    [
      paymentMethodFilters,
      filterDropdownOpen,
      handlePdfPreviewClick,
      handlePropagatedActionClick,
      buildFileHref
    ]
  )

  const batchSelectionContext = useContext(BatchSelectionContext)

  const onHoldableEOBs =
    eobs
      .filter(eob => batchSelectionContext.selectedEOBIds.includes(eob.id))
      .filter(eob => !eob.is_on_hold) ?? []

  const resumableEOBs =
    eobs
      .filter(eob => batchSelectionContext.selectedEOBIds.includes(eob.id))
      .filter(eob => eob.is_on_hold) ?? []

  const stopLossableEOBs =
    eobs
      .filter(eob => batchSelectionContext.selectedEOBIds.includes(eob.id))
      .filter(eob => !eob.is_flagged_stop_loss) ?? []

  const unstopLossableEOBs =
    eobs
      .filter(eob => batchSelectionContext.selectedEOBIds.includes(eob.id))
      .filter(eob => eob.is_flagged_stop_loss) ?? []

  const putOnHoldMutation = useMutation({
    async mutationFn(variables: { ids: string[]; reason: string }) {
      await $api.post('/eobs/put-on-hold', variables, {
        headers: {
          'Content-Type': 'application/json'
        }
      })
    }
  })

  const resumeMutation = useMutation({
    async mutationFn(variables: { ids: string[] }) {
      await $api.post('/eobs/resume', variables, {
        headers: {
          'Content-Type': 'application/json'
        }
      })
    }
  })

  const flagStopLossMutation = useMutation({
    async mutationFn(variables: { ids: string[] }) {
      await $api.post('/eobs/flag-stop-loss', variables, {
        headers: {
          'Content-Type': 'application/json'
        }
      })
    }
  })

  const unflagStopLossMutation = useMutation({
    async mutationFn(variables: { ids: string[] }) {
      await $api.post('/eobs/unflag-stop-loss', variables, {
        headers: {
          'Content-Type': 'application/json'
        }
      })
    }
  })

  return (
    <>
      <Table
        size='small'
        rowKey='id'
        className='rounded-none payment-files-table [&_.ant-table]:![margin-inline:_0]'
        rowClassName={(record: EOB) => {
          return !record.is_eligible ? 'opacity-50 bg-gray-50' : ''
        }}
        rowSelection={{
          columnWidth: 4.5 * 16,
          hideSelectAll: false,

          selectedRowKeys: batchSelectionContext.selectedEOBIds,

          onSelect(_, __, ___, event) {
            event.stopPropagation()
          },

          onChange(selectedRowKeys) {
            batchSelectionContext.onSelectEOBs(selectedRowKeys as string[])
          },

          getCheckboxProps() {
            return {
              // className: 'hidden'
            }
          }
        }}
        columns={
          !query.isLoading
            ? columns
            : columns.map(column => {
                return {
                  ...column,
                  render: function renderPlaceholder() {
                    return <Skeleton key={column.key} title active={true} paragraph={false} />
                  }
                }
              })
        }
        dataSource={
          !query.isLoading
            ? eobs
            : ([...Array(2)].map((_, index) => ({
                id: `key${index}`
              })) as unknown as EOB[])
        }
        pagination={{
          current: pagination.currentPage,
          pageSize: pagination.pageSize,
          total: query.data?.eobs_total_count ?? 0,
          showSizeChanger: true,
          showTotal: (total, [from, to]) => {
            return (
              <span className='text-sm text-gray-500 font-lato'>
                Showing {from}–{to} of {total} {total === 1 ? 'EOB' : 'EOBs'}
              </span>
            )
          },
          pageSizeOptions: ['10', '25', '50', '100'],
          hideOnSinglePage: false
        }}
        onChange={(paginationConfig, _, __, extra) => {
          if (extra.action === 'paginate') {
            setPagination({
              currentPage: paginationConfig.current ?? 1,
              pageSize: paginationConfig.pageSize ?? 10
            })
          }
        }}
        expandable={{
          expandedRowKeys: state.expandedRowIds,
          onExpandedRowsChange(expandedRowKeys) {
            dispatch({
              type: 'rows/expand',
              payload: expandedRowKeys.map(key => key.toString(10))
            })
          },
          expandIcon: ExpandIcon as RenderExpandIcon<EOB>,
          showExpandColumn: false,
          expandRowByClick: true,
          expandedRowRender(record, _, __, expanded) {
            if (!expanded) {
              return null
            }

            return (
              <div className='p-4 bg-gray-50'>
                <EOBExpandableRow
                  checkRunId={props.checkRunId}
                  groupId={props.groupId}
                  paymentFileId={props.paymentFileId}
                  eobNumber={record.id}
                />
              </div>
            )
          }
        }}
        components={{
          header: {
            wrapper(
              wrapperProps: JSX.IntrinsicAttributes &
                React.ClassAttributes<HTMLTableSectionElement> &
                React.HTMLAttributes<HTMLTableSectionElement>
            ) {
              const token = window.localStorage.getItem('token')

              return (
                <thead {...wrapperProps}>
                  <tr>
                    <td
                      colSpan={columns.length + 1}
                      className='!font-normal !font-lato !text-xs !bg-white'
                    >
                      <div className='flex flex-col gap-3 px-12 py-3'>
                        <div className='flex flex-row gap-4 justify-between items-center bg-white'>
                          <div className='flex flex-col gap-0.5'>
                            <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-gray-500'>
                              Payment File
                            </span>

                            <div className='flex flex-col gap-2'>
                              <a
                                href={`${API_URL}/files/${encodeURIComponent(props.paymentFileName)}?event-type-id=${encodeURIComponent(1)}&file-type-id=${encodeURIComponent(1)}&token=${encodeURIComponent(token ?? '')}`}
                                className='text-sm font-lato'
                              >
                                {props.paymentFileName}
                              </a>
                            </div>
                          </div>

                          {events && events?.sent_to_zelis?.length > 0 && (
                            <div className='flex flex-col gap-0.5'>
                              <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-gray-500'>
                                Sent to Zelis
                              </span>

                              <div className='flex flex-col gap-2'>
                                {events.sent_to_zelis.map(event => (
                                  <a
                                    key={event.id}
                                    href={`${API_URL}/files/${encodeURIComponent(event.filename)}?event-type-id=${encodeURIComponent(2)}&file-type-id=${encodeURIComponent(2)}&token=${encodeURIComponent(token ?? '')}`}
                                    className='text-sm font-lato'
                                  >
                                    {event.filename}
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}

                          {events && events.received_from_zelis.length > 0 && (
                            <div className='flex flex-col gap-0.5'>
                              <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-gray-500'>
                                Received from Zelis
                              </span>

                              <div className='flex flex-col gap-2'>
                                {events.received_from_zelis.map(event => (
                                  <a
                                    key={event.id}
                                    href={`${API_URL}/files/${encodeURIComponent(event.filename)}?event-type-id=${encodeURIComponent(3)}&file-type-id=${encodeURIComponent(3)}&token=${encodeURIComponent(token ?? '')}`}
                                    className='text-sm font-lato'
                                  >
                                    {event.filename}
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>

                  <BatchSelectionPanel
                    colSpan={columns.length + 2}
                    selectedItemsCount={batchSelectionContext.selectedEOBIds.length}
                    pluralization={['EOB', 'EOBs']}
                    onClearSelection={() => {
                      batchSelectionContext.onSelectEOBs([])
                    }}
                  >
                    <div className='flex-grow' />

                    {onHoldableEOBs.length > 0 && (
                      <BatchActionButton
                        onClick={() => {
                          dispatch({
                            type: 'onHoldModal/open',
                            payload: onHoldableEOBs.map(eob => eob.id)
                          })
                        }}
                        title={`${onHoldableEOBs.length} on-holdable ${pluralize(onHoldableEOBs.length, 'EOB', 'EOBs')}`}
                      >
                        Put on Hold
                      </BatchActionButton>
                    )}

                    {resumableEOBs.length > 0 && (
                      <BatchActionButton
                        loading={resumeMutation.isPending}
                        onClick={() => {
                          resumeMutation.mutate(
                            {
                              ids: resumableEOBs.map(eob => eob.id)
                            },
                            {
                              onError(err) {
                                notification.error({
                                  message: 'Failed to resume EOBs',
                                  description:
                                    err instanceof Error ? err.message : 'Something went wrong'
                                })
                              },
                              async onSuccess() {
                                queryClient.invalidateQueries({
                                  predicate: query =>
                                    (query.queryKey[0] === 'check-run-payment-files' ||
                                      query.queryKey[0] === 'check-run-totals' ||
                                      query.queryKey[0] === 'check-run-eobs') &&
                                    (query.queryKey[1] as unknown as { checkRunId: string })
                                      ?.checkRunId === props.checkRunId
                                })
                              }
                            }
                          )
                        }}
                        title={`${resumableEOBs.length} resumable ${pluralize(resumableEOBs.length, 'EOB', 'EOBs')}`}
                      >
                        Resume
                      </BatchActionButton>
                    )}

                    {stopLossableEOBs.length > 0 && (
                      <BatchActionButton
                        loading={flagStopLossMutation.isPending}
                        onClick={() => {
                          flagStopLossMutation.mutate(
                            {
                              ids: stopLossableEOBs.map(eob => eob.id)
                            },
                            {
                              onError(err) {
                                notification.error({
                                  message: 'Failed to flag EOBs as stop loss',
                                  description:
                                    err instanceof Error ? err.message : 'Something went wrong'
                                })
                              },
                              async onSuccess() {
                                queryClient.invalidateQueries({
                                  predicate: query =>
                                    (query.queryKey[0] === 'check-run-payment-files' ||
                                      query.queryKey[0] === 'check-run-totals' ||
                                      query.queryKey[0] === 'check-run-eobs') &&
                                    (query.queryKey[1] as unknown as { checkRunId: string })
                                      ?.checkRunId === props.checkRunId
                                })
                              }
                            }
                          )
                        }}
                        title={`${stopLossableEOBs.length} stop-lossable ${pluralize(stopLossableEOBs.length, 'EOB', 'EOBs')}`}
                      >
                        Flag Stop Loss
                      </BatchActionButton>
                    )}

                    {unstopLossableEOBs.length > 0 && (
                      <BatchActionButton
                        loading={unflagStopLossMutation.isPending}
                        onClick={() => {
                          unflagStopLossMutation.mutate(
                            {
                              ids: unstopLossableEOBs.map(eob => eob.id)
                            },
                            {
                              onError(err) {
                                notification.error({
                                  message: 'Failed to unflag EOBs as stop loss',
                                  description:
                                    err instanceof Error ? err.message : 'Something went wrong'
                                })
                              },
                              async onSuccess() {
                                queryClient.invalidateQueries({
                                  predicate: query =>
                                    (query.queryKey[0] === 'check-run-payment-files' ||
                                      query.queryKey[0] === 'check-run-totals' ||
                                      query.queryKey[0] === 'check-run-eobs') &&
                                    (query.queryKey[1] as unknown as { checkRunId: string })
                                      ?.checkRunId === props.checkRunId
                                })
                              }
                            }
                          )
                        }}
                        title={`${unstopLossableEOBs.length} unstop-lossable ${pluralize(unstopLossableEOBs.length, 'EOB', 'EOBs')}`}
                      >
                        Remove Stop Loss
                      </BatchActionButton>
                    )}
                  </BatchSelectionPanel>

                  {wrapperProps.children}
                </thead>
              )
            }
          }
        }}
      />

      <Modal
        title={`${onHoldableEOBs.length} ${pluralize(onHoldableEOBs.length, 'EOB', 'EOBs')} to Put on Hold`}
        open={state.onHoldModal.isOpen}
        okText={
          state.onHoldModal.isSubmitting
            ? 'Putting on Hold...'
            : state.onHoldModal.error
              ? 'Retry'
              : 'Put on Hold'
        }
        width={800}
        onOk={() => {
          const startedAt = Date.now()
          const minWaitTime = 2000

          dispatch({
            type: 'onHoldModal/submit'
          })

          putOnHoldMutation.mutate(
            {
              ids: state.onHoldModal.eobIds,
              reason: state.onHoldModal.form.reason || 'No reason provided'
            },
            {
              async onSuccess() {
                setTimeout(
                  () => {
                    dispatch({
                      type: 'onHoldModal/submit/success'
                    })
                  },
                  Math.max(minWaitTime - (Date.now() - startedAt), 0)
                )

                queryClient.invalidateQueries({
                  predicate: query =>
                    (query.queryKey[0] === 'check-run-payment-files' ||
                      query.queryKey[0] === 'check-run-totals' ||
                      query.queryKey[0] === 'check-run-eobs') &&
                    (query.queryKey[1] as unknown as { checkRunId: string })?.checkRunId ===
                      props.checkRunId
                })
              },
              onError(error) {
                dispatch({
                  type: 'onHoldModal/submit/error',
                  payload: error instanceof Error ? error : new Error('Something went wrong')
                })
              }
            }
          )
        }}
        closable={false}
        confirmLoading={state.onHoldModal.isSubmitting}
        onCancel={() => {
          dispatch({
            type: 'onHoldModal/close'
          })
        }}
      >
        <div className='flex flex-col gap-4 pb-4'>
          {state.onHoldModal.error && (
            <Alert
              type='error'
              message={state.onHoldModal.error.message}
              className='mt-2'
              showIcon
            />
          )}

          <p>
            Provide a reason for <strong>putting</strong>{' '}
            {pluralize(onHoldableEOBs.length, 'this EOB', 'these EOBs')} <strong>on hold</strong>.
          </p>

          <Input.TextArea
            rows={4}
            placeholder='Reason'
            className='font-lato'
            id='on-hold-reason'
            value={state.onHoldModal.form.reason}
            onChange={event => {
              dispatch({
                type: 'onHoldModal/form/reason/set',
                payload: event.target.value
              })
            }}
          />

          <p>
            Are you sure you want to <strong>put on hold</strong>{' '}
            {pluralize(onHoldableEOBs.length, 'this EOB', 'these EOBs')}?
          </p>
        </div>
      </Modal>
    </>
  )
}
